import React, { useEffect, useState } from 'react'
import {
  CompanyId,
  GetCompanyEmployees$Request,
  GetCompanyStatisticsByPeriod$Request,
  Lang,
  Role,
  SetCompanyEmployees$Request,
  Stats,
  UserCompanyDto,
} from '../../services/api.types'
import { Card } from '../../components/Card/Card'
import { t } from 'ttag'
import moment from 'moment/moment'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import { Grid } from '@material-ui/core'
import { currentLang, isRtl } from '../../i18nInit'
import YumSmallSelect from '../../components/YumSmallSelect/YumSmallSelect'
import StatCard from './components/StatCard/StatCard'
import { Link, Redirect } from 'react-router-dom'
import { ROUTES } from '../../common/Routes/routePaths'
import './Dashboard.scss'
import { Dialog } from '@capacitor/dialog'
import { yuMuuvPublicCompanyId } from '../Settings/Settings'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { getShortDateWithTimeString } from '../../common/helpers/date'

export interface IDashboardStateProps {
  userCompanies: UserCompanyDto[]
  employees: { [companyId: CompanyId]: number }
  companyDashboard: Stats[]
}

export interface IDashboardDispatchProps {
  getSpecificCompanyDashboard(payload: GetCompanyStatisticsByPeriod$Request): void
  getCompanyEmployees(payload: GetCompanyEmployees$Request): void
  setCompanyEmployees(payload: SetCompanyEmployees$Request & GetCompanyStatisticsByPeriod$Request): void
  hasRoleInCompany(companyId: CompanyId, role: Role): boolean
}

type IDashboardProps = IDashboardStateProps & IDashboardDispatchProps

const getDefaultMonth = () => {
  const now = moment().utc().locale('en')
  const day = parseInt(now.clone().format('D'))
  let month = day > 1 ? now.clone() : now.clone().subtract(1, 'months')
  if (currentLang === Lang.EN) {
    month = month.locale('en')
  }
  return month
}

const getMonthsList = () => {
  let dateStart = moment('2023-01-01')
  if (currentLang === Lang.EN) {
    dateStart = dateStart.locale('en')
  }
  const dateEnd = getDefaultMonth()
  const interim = dateStart.clone()
  const timeValues: any = []

  while (dateEnd > interim || interim.format('M') === dateEnd.format('M')) {
    const interimLocalized = interim.clone().locale('en')
    timeValues.push({
      value: interimLocalized.format('YYYY-MM'),
      label: interim.format('MMM YYYY').replace(/^\w/, (c) => c.toUpperCase()),
    })
    interim.add(1, 'month')
  }

  return timeValues.reverse()
}

/*const shouldShowReport = () =>
  this.props.userCompanies && !this.props.userCompanies.some((uc) => !uc.hideStatisticsView)*/

const useStyles = makeStyles((theme) => ({
  header: {
    textAlign: 'left',
    marginBottom: '40px',
    [theme.breakpoints.up('md')]: {
      textAlign: 'right',
      marginBottom: '0px',
    },
  },
  h2: {
    fontWeight: 700,
    fontSize: '24px',
    lineHeight: '29px',
    marginTop: '0px',
    marginBottom: '16px',
  },
  grid: {
    display: 'grid',
    gap: '20px',
    gridTemplateColumns: 'none',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
  },
}))

export default function Dashboard(props: IDashboardProps) {
  const classes = useStyles()
  const {
    userCompanies: userCompaniesRaw,
    employees,
    companyDashboard,
    getSpecificCompanyDashboard,
    getCompanyEmployees,
    setCompanyEmployees,
    hasRoleInCompany,
  } = props

  const userCompanies = userCompaniesRaw.filter((company) => {
    const adminAccess = company.reportRequired && hasRoleInCompany(company.companyId, Role.ADMIN)
    const userAccess = company.reportRequired && company.reportRequiredForUsers
    return company.companyId !== yuMuuvPublicCompanyId && (adminAccess || userAccess)
  })

  if (!userCompanies || userCompanies.length === 0) {
    return <Redirect to={ROUTES.CLIENT_STATISTICS.to} />
  }

  const [monthsList, setMonthsList] = useState(getMonthsList())
  const [month, setMonth] = useState(getDefaultMonth().format('YYYY-MM'))
  const [companyId, setCompanyId] = useState(userCompanies && userCompanies[0]?.companyId)
  const employeeCount = employees ? employees[companyId] : false

  const [statsSelectedMonth, setStatsSelectedMonth] = useState([] as any)
  const [statsPreviousMonth, setStatsPreviousMonth] = useState([] as any)

  useEffect(() => {
    const currentMoment = moment(`${month}-01`).locale('en')
    const selectedMonthStartDate = currentMoment.clone().format('YYYY-MM-DD')
    const previousMonthStartDate = currentMoment.clone().subtract(1, 'month').format('YYYY-MM-DD')
    setStatsSelectedMonth(
      companyDashboard
        .filter((stat) => stat.startDate === selectedMonthStartDate)
        .reduce((obj, item) => Object.assign(obj, { [item.type]: item }), {}),
    )
    setStatsPreviousMonth(
      companyDashboard
        .filter((stat) => stat.startDate === previousMonthStartDate)
        .reduce((obj, item) => Object.assign(obj, { [item.type]: item }), {}),
    )
  }, [companyDashboard])

  const updateData = () => {
    const selectedMonth = moment(`${month}-01`)
    const startDate = selectedMonth.clone().subtract(1, 'months').locale('en').format('YYYY-MM-DD')
    const endDate = selectedMonth.clone().endOf('month').locale('en').format('YYYY-MM-DD')
    getSpecificCompanyDashboard({
      companyId,
      period: 'MONTH',
      startDate,
      endDate,
    })
  }

  useEffect(() => {
    updateData()
  }, [companyId, month])

  useEffect(() => {
    if (hasRoleInCompany(companyId, Role.ADMIN)) {
      getCompanyEmployees({ companyId })
    }
  }, [companyId])

  const handleMonthChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setMonth(event.target.value as string)
  }

  const handleCompanyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setCompanyId(event.target.value as string)
  }

  const getStats = (type: string, type2?: string, type3?: string) => {
    const current = statsSelectedMonth[type] || {
      type,
      result: null,
    }
    const previous = statsPreviousMonth[type] || {
      type,
      result: null,
    }
    if (!type2 || !type3) {
      return {
        current,
        previous,
      }
    } else {
      return {
        current,
        previous,
        current2: statsSelectedMonth[type2] || {
          type: type2,
          result: null,
        },
        previous2: statsSelectedMonth[type3] || {
          type: type3,
          result: null,
        },
      }
    }
  }

  const editEmployees = async () => {
    const formattedMonth = moment(month).format('MMM YYYY')
    const { value, cancelled } = await Dialog.prompt({
      title: '',
      message: `${t`Employees`} (${formattedMonth})`,
      inputText: employeeCount ? String(employeeCount) : '',
    })
    if (cancelled) return

    // const defaultMonth = getDefaultMonth()
    const selectedMonth = moment(`${month}-01`)
    const startDate = selectedMonth.clone().subtract(1, 'months').locale('en').format('YYYY-MM-DD')
    const endDate = selectedMonth.clone().endOf('month').locale('en').format('YYYY-MM-DD')
    setCompanyEmployees({
      companyId,
      employees: parseInt(value),
      month,

      period: 'MONTH',
      startDate,
      endDate,
    })
    /*if (month !== defaultMonth.format('YYYY-MM')) {
      setMonth(defaultMonth.format('YYYY-MM'))
    }*/
  }

  const mostAdvancedUsers = getStats('COMPANY_MOST_ADVANCED_USERS')
  const mostEngagedUsers = getStats('COMPANY_MOST_ENGAGED_USERS')
  const mostActiveUsers = getStats('COMPANY_MOST_ACTIVE_USERS')
  const showTop10 = mostAdvancedUsers.current.data || mostEngagedUsers.current.data || mostActiveUsers.current.data

  const lastUpdated = statsSelectedMonth['COMPANY_TOTAL_USERS']?.updatedAt

  const viewFunFacts = userCompanies && !userCompanies.filter((c) => c.companyId === companyId)[0]?.hideStatisticsView

  return (
    <Card>
      <NotificationsWrapper />
      <Grid
        container
        style={{ marginBottom: '1rem' }}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        wrap="nowrap"
      >
        <Grid item style={{ flexShrink: '0', paddingRight: isRtl ? 0 : '10px', paddingLeft: isRtl ? '10px' : 0 }}>
          <h1 style={{ marginBottom: '3px' }}>{t`Statistics`}</h1>
        </Grid>
        <Grid item style={{ overflow: 'hidden' }}>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            style={{ flexWrap: 'nowrap' }}
          >
            {/*{userCompanies.length > 1 ? (*/}
            <Grid
              item
              style={{ overflow: 'hidden', paddingRight: isRtl ? 0 : '10px', paddingLeft: isRtl ? '10px' : 0 }}
            >
              <YumSmallSelect variant="outlined" value={companyId} onChange={handleCompanyChange}>
                {userCompanies.map((userCompany: UserCompanyDto, keyIndex: number) => (
                  <option key={keyIndex} value={userCompany.companyId}>
                    {userCompany.companyName}
                  </option>
                ))}
              </YumSmallSelect>
            </Grid>
            {/*) : (
              ''
            )}*/}
            <Grid item style={{ flexShrink: '0' }}>
              <YumSmallSelect value={month} onChange={handleMonthChange}>
                {monthsList.map((month: any, keyIndex: number) => (
                  <option key={keyIndex} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </YumSmallSelect>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/*<YumGrid>
        <div style={{ marginBottom: '20px' }}>Some helpful text...</div>
        <Link className={'link-blue'} to={ROUTES.CLIENT_STATISTICS.to}>{t`View fun facts`}</Link>
      </YumGrid>*/}

      {viewFunFacts && (
        <div className={classes.header}>
          <Link className={'link-blue'} to={ROUTES.CLIENT_STATISTICS.to}>{t`View fun facts`}</Link>
        </div>
      )}

      <h2 className={classes.h2}>{[Lang.EN, Lang.ET, Lang.RU].includes(currentLang) ? t`Users` : `Users`}</h2>
      <div className={classes.grid} style={{ marginBottom: '32px' }}>
        <StatCard
          stats={getStats('COMPANY_EMPLOYEES')}
          editEmployees={hasRoleInCompany(companyId, Role.ADMIN) ? editEmployees : undefined}
        />
        <StatCard stats={getStats('COMPANY_TOTAL_USERS')} />
        <StatCard stats={getStats('COMPANY_ACTIVE_USERS')} />

        <StatCard stats={getStats('COMPANY_TOTAL_USERS_FROM_EMPLOYEES_PERCENTAGE')} />
        <StatCard stats={getStats('COMPANY_ACTIVE_USERS_FROM_TOTAL_PERCENTAGE')} />
        <StatCard stats={getStats('COMPANY_HR_USERS_PERCENTAGE')} />

        {showTop10 ? (
          <>
            <StatCard stats={mostActiveUsers} />
            <StatCard stats={mostAdvancedUsers} />
            <StatCard stats={mostEngagedUsers} />
          </>
        ) : (
          <></>
        )}
      </div>

      <h2 className={classes.h2}>{t`Activity`}</h2>
      <div className={classes.grid}>
        <StatCard stats={getStats('COMPANY_STEPS_PER_USER_PER_DAY')} />
        <StatCard stats={getStats('COMPANY_EXEMPLARY_ACTIVE')} />
        <StatCard stats={getStats('COMPANY_MEDIAN_ACTIVITY')} />

        {/*<StatCard stats={getStats('COMPANY_REGISTERED_USERS')} />*/}

        <StatCard stats={getStats('COMPANY_TOTAL_STEPS')} />
        <StatCard stats={getStats('COMPANY_TOTAL_CALORIES')} />
        <StatCard stats={getStats('COMPANY_TOTAL_MINUTES')} />

        {/*<StatCard stats={getStats('sector')} />
        <div>[Most active group]</div>
        <StatCard stats={getStats('most active group')} />*/}

        {/*<div>?</div>*/}
        <StatCard
          stats={getStats('COMPANY_MOST_ACTIVE_DAY', 'COMPANY_MOST_ACTIVE_DAY_MEDIAN', 'COMPANY_MEDIAN_ACTIVITY')}
        />
        {/*<StatCard stats={getStats('COMPANY_CHALLENGES_STARTED')} />
        <StatCard stats={getStats('COMPANY_CHALLENGES_ENDED')} />*/}
        <StatCard stats={getStats('COMPANY_CHALLENGES')} />
        <StatCard stats={getStats('COMPANY_CHALLENGES_PARTICIPANTS')} />

        {/*<div style={{ marginTop: '50px' }}>New:</div>
        <div></div>
        <div></div>

        <StatCard stats={getStats('COMPANY_USER_COMPANIES_CREATED')} />
        <StatCard stats={getStats('COMPANY_USER_COMPANIES_REGISTERED_USERS')} />*/}
      </div>
      {lastUpdated && (
        <div /*className="text-right"*/ style={{ marginTop: 20, opacity: 0.5, fontSize: '14px' }}>
          {t`Updated`}: {getShortDateWithTimeString(lastUpdated)}
        </div>
      )}
      {/*<br />
      <br />+ extra:
      <ul>
        <li>COMPANY_ACTIVE_USERS_FROM_TOTAL_PERCENTAGE</li>
        <li>COMPANY_ACTIVE_USERS_FROM_EMPLOYEES_PERCENTAGE (needs "employees" set)</li>
      </ul>*/}
      {/*{companyDashboard.length > 0 &&
        companyDashboard.map((stats, index) => (
          <div key={index}>
            {getStatLabel(stats.type) === stats.type ? (
              <>
                {getStatLabel(stats.type)}: {stats.result}
              </>
            ) : (
              <></>
            )}
          </div>
        ))}*/}
    </Card>
  )
}
